import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { tap, delay, catchError, shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AdminProfile } from '../../models/profile.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private storageKey = 'auth_token';
  
  // WritableSignal to store the loaded administrator profile
  public readonly currentUserProfile = signal<AdminProfile | null>(null);
  private profileRequest$: Observable<AdminProfile> | null = null;

  constructor(private http: HttpClient) {
    // Auto-load profile on initialization if token is already present
    if (this.isAuthenticated()) {
      this.getProfile().subscribe({
        error: (err) => console.warn('Could not auto-load user profile on startup:', err)
      });
    }
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem(this.storageKey);
  }

  getToken(): string | null {
    return localStorage.getItem(this.storageKey);
  }

  setToken(token: string): void {
    localStorage.setItem(this.storageKey, token);
  }

  clear(): void {
    this.currentUserProfile.set(null);
    this.profileRequest$ = null;
    localStorage.removeItem(this.storageKey);
  }

  // Dynamic login based on environment configuration
  login(credentials: { email: string; password: string }): Observable<any> {
    if (environment.useMockData) {
      return this.mockLogin(credentials.email, credentials.password);
    } else {
      return this.realLogin(credentials);
    }
  }

  // Mock login: simula una llamada y guarda token en storage
  mockLogin(username: string, password: string): Observable<{ token: string; user: any }> {
    const mock = { token: 'mock-token-123', user: { name: username } };
    return of(mock).pipe(delay(500), tap(res => this.setToken(res.token)));
  }

  // Real login: hace la petición real al backend en base al ambiente configurado
  realLogin(credentials: { email: string; password: string }): Observable<any> {
    const url = `${environment.apiUrl}/loginAdminController/loginAdmin`;
    return this.http.post<any>(url, credentials).pipe(
      tap((res: any) => {
        // Formato esperado de respuesta: { status: "OK", data: "JWT_TOKEN" }
        if (res && res.status === 'OK' && res.data) {
          this.setToken(res.data);
        } else if (res && res.token) {
          this.setToken(res.token);
        } else {
          throw new Error('Respuesta de autenticación inválida');
        }
      }),
      catchError(error => {
        console.error('Error de autenticación:', error);
        return throwError(() => error);
      })
    );
  }

  // Forgot password based on environment configuration
  forgotPassword(email: string): Observable<any> {
    if (environment.useMockData) {
      return of({ status: 'OK', data: 'Se ha enviado un correo de recuperación simulado.' }).pipe(delay(500));
    } else {
      const url = `${environment.apiUrl}/loginAdminController/forgot-password`;
      return this.http.post<any>(url, { email });
    }
  }

  // Get Admin Profile from environment-specific backend or mock
  getProfile(): Observable<AdminProfile> {
    if (this.profileRequest$) {
      return this.profileRequest$;
    }

    if (environment.useMockData) {
      const mockProfile: AdminProfile = {
        success: true,
        message: 'Perfil simulado obtenido con éxito',
        adminName: 'Paolo Dance',
        adminLastName: 'Gonzalez',
        email: 'polodeportes@sportitalia.cl',
        sportCenterName: 'Sport Italia Chanco',
        sportCenterId: 1,
        todayHours: '10:00 - 20:00 (Horario regular)',
        sportCenterStatus: 'Activo',
        sportCenterActive: true,
        currentRuleBlock: null,
        totalFacilities: 10,
        activeFacilities: 8,
        inMaintenanceFacilities: 2,
        operabilityPercentage: 80.0,
        notificationCount: 3,
        currentReservationsCount: 3,
        menu: [
          { title: 'Dashboard', icon: 'bi-speedometer2', path: '/dashboard', highlight: null, children: [] },
          { title: 'Canchas', icon: 'bi-geo-alt', path: '/courts', highlight: null, children: [] },
          { title: 'Reglas Horarias', icon: 'bi-clock-history', path: '/time-rules', highlight: null, children: [] },
          { title: 'Reservas', icon: 'bi-calendar-check', path: '/reservations', highlight: null, children: [] },
          { title: 'Calendario', icon: 'bi-calendar-week', path: '/calendar', highlight: null, children: [] },
          { title: 'Cerrar Sesión', icon: 'bi-box-arrow-right', path: '/login', highlight: null, children: [] }
        ],
        weather: {
          city: 'Chanco',
          currentTemp: '12°C',
          currentCondition: 'Despejado',
          currentWindSpeed: '5 km/h',
          todaySunset: '18:00',
          todayUvIndex: 4,
          todayPrecipitationProbability: '0%',
          todayMaxTemp: '18°C',
          tomorrowMaxTemp: '16°C',
          tomorrowMinTemp: '7°C',
          tomorrowCondition: 'Despejado',
          tomorrowUvIndex: 4.5,
          tomorrowPrecipitationProbability: '0%',
          tomorrowSunset: '18:05',
          tomorrowWindSpeed: '8 km/h'
        },
        systemTime: '18:00:00',
        systemDate: 'Domingo, 14 de junio de 2026',
        systemTimeZone: 'America/Santiago',
        mainManager: true,
        colorPrefer: '#7c3aed',
        descriptionHours: 'Sujeto a cambios por festividades'
      };
      this.profileRequest$ = of(mockProfile).pipe(
        delay(200),
        tap(profile => {
          this.currentUserProfile.set(profile);
          this.profileRequest$ = null;
        }),
        catchError(err => {
          this.profileRequest$ = null;
          return throwError(() => err);
        }),
        shareReplay(1)
      );
      return this.profileRequest$;
    } else {
      const token = this.getToken();
      const headers = {
        'Authorization': `Bearer ${token}`
      };
      const url = `${environment.apiUrl}/loginAdminController/profile`;
      this.profileRequest$ = this.http.get<AdminProfile>(url, { headers }).pipe(
        tap((profile: AdminProfile) => {
          this.currentUserProfile.set(profile);
          this.profileRequest$ = null;
        }),
        catchError(error => {
          console.error('Error fetching admin profile:', error);
          this.profileRequest$ = null;
          if (error.status === 401) {
            this.clear();
          }
          return throwError(() => error);
        }),
        shareReplay(1)
      );
      return this.profileRequest$;
    }
  }
}
